import { Col, Row, Spin } from 'antd';
import { ReactComponent as CategoryFour } from 'assets/icons/local_shipping_cat_4.svg';
import { Category1TypeFields, Category1Types } from 'enums/category1Enums';
import { NotificationTypes } from 'enums/NotificationEnums';
import { ActivityDataEntryScopeTabs } from 'enums/ScopeThreeCategoryStatusEnums';
import { Category4 } from 'models/Scope3/category4.model';
import { CategoryItem } from 'models/Scope3/categoryItems.model';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import {
  FieldErrors,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useParams } from 'react-router-dom';
import Category4Service from 'services/Category4Service/category4.service';
import SupplierService from 'services/ValueChainService/supplier.service';
import SharedAccordion from 'shared/components/Accordion';
import EmptyStateScope from 'shared/components/CategoryEmptyState';
import Notification from 'shared/components/Notification';
import ScopeThreeHeader from 'shared/components/Scope3Header';
import { useScopeThreeCategoryUrlMode } from 'shared/hooks/useScopeThreeCategoryUrlMode';
import { useYearLabel } from 'shared/utils/generateYearSpan';
import useRedirect from 'shared/utils/useRedirect';
import styles from './category4and9.module.scss';
import {
  Category9TypeFields,
  Category9Types,
} from 'enums/Scope3category9Enums';
import { category4ValidationSchema } from './category4&9.validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { generatePanelList } from './category4and9PanelUtils';
import { ScopeThreeFormEditProvider } from 'context/ScopeThreeFormEditContext';

export const limitParam = { size: 500 };

const Category4and9 = () => {
  const { id: locationId } = useParams();
  const {
    adeParams,
    isEditView,
    enterEditMode,
    clearEditModeParams,
  } = useScopeThreeCategoryUrlMode();
  const { activeTab, isProcessingFacility } = adeParams;
  const { fetchSuppliers, supplierList } = SupplierService();
  const { loading, getCategory4Data, updateCategory4Data, category4Data } =
    Category4Service();
  const { redirectToCategory1, redirectToActivityDataEntry } = useRedirect();
  const [activeTabKey, setActiveTabKey] = useState<string | string[]>(
    Category1TypeFields.INGREDIENTS
  );
  const [categoryError, setCategoryError] = useState<String[]>([]);

  useEffect(
    () => {
      fetchSuppliers(limitParam);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- supplier preload once when category screen mounts
    []
  );

  const fetchInitialValues = async () => {
    const values = await getCategory4Data({
      locationId: locationId ?? '',
      year: activeTab,
    });
    const modifiedValues: Category4 = values ?? new Category4();
    return {
      ...modifiedValues,
      finishedGoods: values?.finishedGoods?.products?.length
        ? values?.finishedGoods
        : {
            ...modifiedValues.finishedGoods,
            products: [{ ...new CategoryItem(), isTransportedByAir: false }],
          },
    };
  };

  const isCategory1Completed =
    category4Data &&
    (!!category4Data?.ingredients?.length ||
      !!category4Data?.others?.length ||
      !!category4Data?.packagings?.length);

  const methods = useForm<Category4>({
    defaultValues: fetchInitialValues,
    resolver: yupResolver(category4ValidationSchema),
    mode: 'onBlur',
  });

  const { control, handleSubmit, trigger, reset } = methods;

  const handleCancelEdit = async () => {
    const values = await fetchInitialValues();
    reset(values);
    clearEditModeParams();
  };

  const checkForError = (errorObj: FieldErrors<Category4>) => {
    let errorCategoryList: String[] = [];

    if (errorObj.hasOwnProperty(Category1TypeFields.INGREDIENTS)) {
      errorCategoryList.push(Category1Types.INGREDIENTS);
    }
    if (errorObj.hasOwnProperty(Category1TypeFields.PACKAGING_MATERIALS)) {
      errorCategoryList.push(Category1Types.PACKAGING_MATERIALS);
    }
    if (errorObj.hasOwnProperty(Category1TypeFields.OTHERS)) {
      errorCategoryList.push(Category1Types.OTHERS);
    }
    if (errorObj.hasOwnProperty(Category9TypeFields.FINISHED_GOODS)) {
      errorCategoryList.push(Category9Types.FINISHED_GOODS);
    }

    return errorCategoryList;
  };

  useEffect(() => {
    const errorCategoryList = checkForError(methods.formState.errors);
    if (!errorCategoryList) return;

    setCategoryError(errorCategoryList);
    if (errorCategoryList.length > 0) {
      Notification({
        message: `There are some error in the ${errorCategoryList.join(', ')}`,
        description: '',
        type: NotificationTypes.ERROR,
      });
    }
  }, [methods.formState.errors]);

  const { fields: ingredientFields } = useFieldArray({
    control,
    name: Category1TypeFields.INGREDIENTS,
  });

  const { fields: packagingFields } = useFieldArray({
    control,
    name: Category1TypeFields.PACKAGING_MATERIALS,
  });

  const { fields: otherFields } = useFieldArray({
    control,
    name: Category1TypeFields.OTHERS,
  });

  const {
    fields: finishedGoodsFields,
    remove,
    append,
  } = useFieldArray({
    control,
    name: 'finishedGoods.products',
  });

  const addProduct: MouseEventHandler = event => {
    if (!isEditView) return;
    event.stopPropagation();
    append({ ...new CategoryItem(), isTransportedByAir: false });
    setActiveTabKey('finishedGoods.products');
    trigger('finishedGoods');
  };

  const panelList = generatePanelList({
    ingredientFields,
    packagingFields,
    otherFields,
    finishedGoodsFields,
    categoryError,
    supplierList,
    fetchSuppliers,
    addProduct,
    remove,
    isEditView,
  });

  const onSubmit = async (values: Category4) => {
    if (!isCategory1Completed || !locationId || !activeTab) return;
    const updatedPayload: Category4 = {
      ...values,
      finishedGoods:
        values?.finishedGoods?.weightTransported &&
        values?.finishedGoods?.weightTransported > 0
          ? values?.finishedGoods
          : null,
    };
    const response = await updateCategory4Data(updatedPayload, {
      locationId,
      year: activeTab,
    });
    if (!response) return;
    redirectToActivityDataEntry(
      locationId,
      {
        activeTab,
        scopeTab: ActivityDataEntryScopeTabs.SCOPE_3_UPSTREAM,
      },
      true
    );
  };

  const handleRedirect = () =>
    //The true is for replace, so that the user would be taken back from Category 1 to ADE and not back to Category 4
    locationId &&
    redirectToCategory1(locationId, { activeTab, isProcessingFacility }, true);

  const emptyStateProps = {
    handleRedirect,
    buttonTitle: 'Category 1',
    title: 'Complete Category 1 first to proceed with Category 4',
    subString: `Oops! It looks like you've skipped a step. Please complete Category 1 first to proceed with Category 4. This ensures we have all the necessary information for calculation`,
  };

  const handleAccordionChange = (key: string | string[]) =>
    setActiveTabKey(key);

  return (
    <ScopeThreeFormEditProvider isEditView={isEditView}>
      <div className={styles.pageWrapper}>
        <Spin spinning={!!loading} className={styles.loader}>
          <FormProvider {...methods}>
            <Row className={styles.contentContainer}>
              <Col span={24} className={styles.contentWrapper}>
                <ScopeThreeHeader
                  icon={<CategoryFour />}
                  title="Category 4 & 9 - Upstream Transportation And Distribution"
                  year={useYearLabel(activeTab)}
                  onSubmit={handleSubmit(onSubmit)}
                  isSubmitDisabled={!isCategory1Completed}
                  isEditView={isEditView}
                  onEnterEdit={enterEditMode}
                  onCancelEdit={handleCancelEdit}
                  loading={!!loading}
                />
              </Col>
            <Col span={24} className={styles.accordianWrapper}>
              {isCategory1Completed || loading ? (
                <SharedAccordion
                  panels={panelList}
                  defaultActiveKey={[Category1TypeFields.INGREDIENTS]}
                  expandIconPosition="end"
                  accordion
                  activeKey={activeTabKey}
                  onChange={handleAccordionChange}
                />
              ) : (
                <div className={styles.emptyStateWrapper}>
                  <EmptyStateScope {...emptyStateProps} />
                </div>
              )}
            </Col>
          </Row>
        </FormProvider>
      </Spin>
      </div>
    </ScopeThreeFormEditProvider>
  );
};

export default Category4and9;
